import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getProduct, priceForQuantity } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import MockupPreview from "@/components/MockupPreview";
import Customizer from "@/components/Customizer";
import Footer from "@/components/Footer";
import { Minus, Plus, ArrowLeft, Check } from "lucide-react";

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, customization, updateSide } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(15);
  const [color, setColor] = useState(null);
  const [side, setSide] = useState("front");

  useEffect(() => {
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        setQty(p.tiers[1]?.min_qty || 1);
        setColor(p.colors?.[0] || null);
        setSide("front");
      })
      .catch(() => navigate("/"));
  }, [slug, navigate]);

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm">Chargement…</div>;
  }

  const unit = priceForQuantity(product.tiers, qty);
  const total = unit * qty;
  const activeTier = product.tiers.filter((t) => qty >= t.min_qty).reduce((a, b) => (b.min_qty > a.min_qty ? b : a), product.tiers[0]);
  const sideImage = (side === "front" ? product.image_front : product.image_back) || product.image;
  const c = customization[side];

  const handleAdd = () => {
    addItem(product.slug, qty, color?.name);
    toast.success(`${qty} × ${product.name}${color ? " · " + color.name : ""} ajouté au devis`);
  };

  return (
    <div className="grain">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 pb-24 md:pb-32">
        <Link to="/#catalogue" data-testid="back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#555] hover:text-black mb-12">
          <ArrowLeft className="w-4 h-4" /> Catalogue
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: preview + customizer */}
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[4/5] bg-white border border-[#e5e5e5]"
              data-testid="mockup-preview"
            >
              <MockupPreview
                image={sideImage}
                colorHex={color?.hex}
                colorName={color?.name}
                logoUrl={c.logo?.url}
                logoPlacement={c.logoPlacement}
                onLogoPlacement={(pl) => updateSide(side, { logoPlacement: pl })}
                text={c.text}
                textPlacement={c.textPlacement}
                textColor={c.textColor}
                onTextPlacement={(pl) => updateSide(side, { textPlacement: pl })}
                className="w-full h-full"
              />
            </motion.div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {[{ k: "front", l: "Devant" }, { k: "back", l: "Dos" }].map((sd) => (
                <button
                  key={sd.k}
                  data-testid={`side-${sd.k}`}
                  onClick={() => setSide(sd.k)}
                  className={`py-3 font-bold uppercase tracking-widest text-xs border transition-colors ${side === sd.k ? "bg-black text-white border-black" : "bg-white border-[#ccc] hover:border-black"}`}
                >
                  {sd.l}
                </button>
              ))}
            </div>

            <div className="mt-6 border border-[#e5e5e5] bg-[#f9f9f8] p-5">
              <p className="font-display font-bold text-lg mb-4">Personnalisation · {side === "front" ? "Devant" : "Dos"}</p>
              <Customizer side={side} />
            </div>
          </div>

          {/* Right: config */}
          <div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl">
              {product.name}
            </motion.h1>
            <p className="text-[#0044ff] font-mono text-sm uppercase tracking-widest mt-3">{product.tagline}</p>
            <p className="text-[#555] text-lg leading-relaxed mt-6 max-w-md">{product.description}</p>

            {product.colors?.length > 0 && (
              <div className="mt-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-3">
                  Couleur · <span className="text-black">{color?.name}</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((col) => (
                    <button
                      key={col.name}
                      data-testid={`color-${col.name}`}
                      onClick={() => setColor(col)}
                      title={col.name}
                      className={`w-9 h-9 rounded-full border transition-all ${color?.name === col.name ? "ring-2 ring-offset-2 ring-[#0044ff] border-transparent" : "border-[#ccc] hover:scale-110"}`}
                      style={{ background: col.hex }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-4">Tarif dégressif · pièce</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {product.tiers.map((t) => {
                  const on = activeTier.min_qty === t.min_qty;
                  return (
                    <div key={t.min_qty} data-testid={`tier-${t.min_qty}`} className={`border p-3 text-center transition-colors ${on ? "bg-[#0044ff] text-white border-[#0044ff]" : "bg-white border-[#e5e5e5]"}`}>
                      <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">{t.min_qty === 1 ? "1 pièce" : `dès ${t.min_qty}`}</p>
                      <p className="font-display font-bold text-2xl mt-1">{t.price}€</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 border-t border-[#e5e5e5] pt-8">
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-3">Quantité</p>
                  <div className="flex items-center border border-black">
                    <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-12 h-12 flex items-center justify-center hover:bg-black hover:text-white transition-colors"><Minus className="w-4 h-4" /></button>
                    <input data-testid="qty-input" type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 h-12 text-center font-display font-bold text-xl outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="w-12 h-12 flex items-center justify-center hover:bg-black hover:text-white transition-colors"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-1">Total estimé · {unit}€ / pièce</p>
                  <p data-testid="product-total" className="font-display font-black text-5xl tracking-tighter">{total.toLocaleString("fr-FR")}€</p>
                </div>
              </div>

              <button data-testid="add-to-quote" onClick={handleAdd} className="w-full mt-8 bg-black text-white py-5 font-bold uppercase tracking-widest text-sm hover:bg-[#0044ff] transition-colors flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Ajouter au devis
              </button>
              <p className="text-center text-xs text-[#555] mt-4">Gratuit et sans engagement · logo &amp; texte différents devant/dos possibles</p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
